import type {ActionReducerMapBuilder} from '@reduxjs/toolkit';
import {
  fetchCampaignRecipientInsights,
  initEmailMarketingCampaignPerformance,
  removeInvalidEmailsForCampaign,
  removeInvalidEmailsForMailingList,
} from './email-marketing-performance-thunk';
import {LOADING_STATES} from '@Libraries/email-marketing-campaigns-library';
import {getDefaultRecipientInsightsPaging, getFirstSelectedItemId} from './email-marketing-campaign-performance-library';
import type {EmailMarketingCampaignPerformanceState, RecipientInsightsPaging} from './email-marketing-campaign-performance.types';

function resetRecipientInsightsPagingState(state: EmailMarketingCampaignPerformanceState): void {
  state.detailsLoadingState = LOADING_STATES.NOT_LOADED;
  state.detailsTotalRows = 0;
  state.detailsRequestedPaging = getDefaultRecipientInsightsPaging();
  state.detailsLoadedRowsPaging = getDefaultRecipientInsightsPaging();
  state.detailsLatestRequestId = '';
}

function isStaleRecipientInsightsResponse(state: EmailMarketingCampaignPerformanceState, requestId: string, campaignId: string): boolean {
  return requestId !== state.detailsLatestRequestId || campaignId !== getFirstSelectedItemId(state.selectedItemIds);
}

export const emailMarketingCampaignPerformanceExtraReducers = (builder: ActionReducerMapBuilder<EmailMarketingCampaignPerformanceState>): void => {
  builder.addCase(initEmailMarketingCampaignPerformance.pending, (state) => {
    state.performanceLoadingState = LOADING_STATES.LOADING;
  });

  builder.addCase(initEmailMarketingCampaignPerformance.fulfilled, (state, {payload}) => {
    state.campaigns = payload.campaigns;
    state.campaignsWithMetricsAvailable = payload.campaignsWithMetricsAvailable;
    state.campaignsWithMetricsNotAvailable = payload.campaignsWithMetricsNotAvailable;
    state.campaignsWithMetricsCollecting = payload.campaignsWithMetricsCollecting;
    state.campaignMetrics = payload.campaignMetrics;
    state.mailingLists = payload.mailingLists;
    state.mailingListsWithAvailableMetrics = payload.mailingListsWithAvailableMetrics;
    state.mailingListsWithMetricsNotAvailable = payload.mailingListsWithMetricsNotAvailable;
    state.mailingListsWithMetricsCollecting = payload.mailingListsWithMetricsCollecting;
    state.mailingListMetrics = payload.mailingListMetrics;
    state.itemType = payload.itemType;
    state.selectedItemIds = payload.selectedItemIds.map((id) => String(id));
    state.isNoCampaignCreated = payload.isNoCampaignCreated;
    state.isNoCampaignPublished = payload.isNoCampaignPublished;
    state.isCollectingData = payload.isCollectingData;
    state.isNoMailingListCreated = payload.isNoMailingListCreated;
    state.isNotCollectingData = payload.isNotCollectingData;
    state.performanceLoadingState = LOADING_STATES.LOADED;
    resetRecipientInsightsPagingState(state);
  });

  builder.addCase(fetchCampaignRecipientInsights.pending, (state, {meta}) => {
    state.detailsLoadingState = LOADING_STATES.LOADING;
    state.detailsRequestedPaging = {
      offset: meta.arg.offset,
      limit: meta.arg.limit,
      sortField: meta.arg.sortField,
      sortDir: meta.arg.sortDir,
    };
    state.detailsLatestRequestId = meta.requestId;
  });

  builder.addCase(fetchCampaignRecipientInsights.fulfilled, (state, {payload, meta}) => {
    if (isStaleRecipientInsightsResponse(state, meta.requestId, meta.arg.campaignId)) {
      return;
    }
    state.detailsLoadingState = LOADING_STATES.LOADED;
    if (payload.totalRows !== null) {
      state.detailsTotalRows = payload.totalRows;
    }
    const fetchedPaging: RecipientInsightsPaging = {
      offset: payload.offset,
      limit: payload.limit,
      sortField: meta.arg.sortField,
      sortDir: meta.arg.sortDir,
    };
    state.detailsRequestedPaging = fetchedPaging;
    state.detailsLoadedRowsPaging = {...fetchedPaging};

    const metricsForCampaign = state.campaignMetrics[meta.arg.campaignId];
    if (metricsForCampaign === undefined) {
      return;
    }
    metricsForCampaign.details = payload.details;
  });

  builder.addCase(fetchCampaignRecipientInsights.rejected, (state, {meta}) => {
    if (isStaleRecipientInsightsResponse(state, meta.requestId, meta.arg.campaignId)) {
      return;
    }
    state.detailsLoadingState = LOADING_STATES.LOADED;
    state.detailsRequestedPaging = {...state.detailsLoadedRowsPaging};
  });

  builder.addCase(removeInvalidEmailsForCampaign.pending, (state) => {
    state.performanceLoadingState = LOADING_STATES.LOADING;
  });

  builder.addCase(removeInvalidEmailsForCampaign.fulfilled, (state, {meta}) => {
    state.performanceLoadingState = LOADING_STATES.LOADED;
    const firstId = getFirstSelectedItemId(state.selectedItemIds);
    if (!firstId) {
      return;
    }
    meta.arg.emails.forEach((email) => {
      const detailsRow = state.campaignMetrics[firstId].details[email];
      if (detailsRow !== undefined) {
        detailsRow.isDeleted = true;
      }
    });
  });

  builder.addCase(removeInvalidEmailsForMailingList.pending, (state) => {
    state.performanceLoadingState = LOADING_STATES.LOADING;
  });

  builder.addCase(removeInvalidEmailsForMailingList.fulfilled, (state, {meta}) => {
    state.performanceLoadingState = LOADING_STATES.LOADED;
    const firstId = getFirstSelectedItemId(state.selectedItemIds);
    if (!firstId) {
      return;
    }
    meta.arg.emails.forEach((email) => {
      state.mailingListMetrics[firstId].insights[email].isDeleted = true;
    });
  });
};
